<template>
  <div class="monthly-view">
    <div class="month-header">
      <div class="day-header" v-for="day in daysOfWeek" :key="day">
        {{ day }}
      </div>
    </div>
    <div class="month-grid">
      <div 
        v-for="day in monthDays" 
        :key="day.date.toString()"
        class="month-day"
        :class="{ 
          'current-month': day.isCurrentMonth,
          'today': day.isToday,
          'weekend': day.isWeekend,
        }"
      >
        <div class="day-number">{{ day.date.getDate() }}</div>
        <div 
          v-for="session in getSessionsForDay(day.date)" 
          :key="session.session_id"
          class="session-item"
          @click="$emit('session-click', session)"
        >
          <div class="session-time">
            {{ formatTime(session.start_at) }} - {{ formatTime(session.end_at) }}
          </div>
          <div class="session-course">{{ session.course.name }}</div>
          <div class="session-status">
            <div 
              v-if="session.absentStudents.length > 0" 
              class="session-absent"
              :class="{ 'all-completed': session.absentStudents.length === session.completedMakeupClassStudents?.length }"
            >
              {{ session.absentStudents.length }} absent
            </div>
            <div 
              v-if="session.completedMakeupClassStudents?.length > 0" 
              class="makeup-completed"
              :title="`${session.completedMakeupClassStudents.length} makeup classes completed`"
            >
              ✓ {{ session.completedMakeupClassStudents.length }}/{{ session.absentStudents.length }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  sessions: Array,
  currentDate: Date
});

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const monthDays = computed(() => {
  const year = props.currentDate.getFullYear();
  const month = props.currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  const startDay = (firstDay.getDay() + 6) % 7; // Adjust to start from Monday
  const daysInMonth = lastDay.getDate();
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const days = [];
  
  // Add previous month days
  const prevMonthDays = startDay;
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = prevMonthDays - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, prevMonthLastDay - i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.toDateString() === today.toDateString()
    });
  }
  
  // Add current month days
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({
      date,
      isCurrentMonth: true,
      isToday: date.toDateString() === today.toDateString()
    });
  }
  
  // Add next month days
  const totalCells = Math.ceil(days.length / 7) * 7;
  const nextMonthDays = totalCells - days.length;
  for (let i = 1; i <= nextMonthDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      date,
      isCurrentMonth: false,
      isToday: date.toDateString() === today.toDateString()
    });
  }
  
  return days.map(day => ({
    ...day,
    isWeekend: day.date.getDay() === 0 || day.date.getDay() === 6
  }));
});

function getSessionsForDay(day) {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);
  
  return props.sessions.filter(session => {
    const sessionDate = new Date(session.start_at);
    return sessionDate >= dayStart && sessionDate <= dayEnd;
  }).sort((a, b) => {
    return new Date(a.start_at) - new Date(b.start_at);
  });
}

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<style scoped>
.monthly-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.month-header {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background: #f5f5f5;
  font-weight: bold;
  text-align: center;
  padding: 10px 0;
}

.day-header {
  padding: 5px;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 120px;
  flex-grow: 1;
}

.month-day {
  border: 1px solid #eee;
  padding: 5px;
  overflow-y: auto;
}

.month-day.weekend {
  background-color: #fff0f5;
}

.month-day:not(.current-month) {
  background-color: #f9f9f9;
  color: #aaa;
}

.month-day:not(.current-month).weekend {
  background-color: #f3e0e0;
}

.month-day.today {
  background-color: #e6f7ff;
}

.month-day.today.weekend {
  background-color: #e6f7ff;
}

.day-number {
  font-weight: bold;
  margin-bottom: 5px;
}

.session-item {
  background: var(--primary-color);
  color: white;
  padding: 3px 5px;
  margin-bottom: 3px;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
}

.session-item:hover {
  opacity: 0.9;
}

.session-time {
  font-weight: bold;
}

.session-course {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-status {
  display: flex;
  gap: 4px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.session-absent {
  font-size: 0.7rem;
  background: var(--danger-color); /* Default red for incomplete */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}

.session-absent.all-completed {
  background: #D7A3A3; /* Gray when all makeup classes are done */
  color: #5D1F1F;
}

.makeup-completed {
  font-size: 0.7rem;
  background: var(--secondary-color); /* Blue for completed */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}
</style>